import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useMemo } from "react";
import { TPerformanceFunnelOverviewItem } from "../types";
import { EmptyStateMessage } from "./empty-state-message";
import { ErrorStateMessage } from "./error-state-message";

// API response format
interface TReadingDepthItem {
  event: string;
  percentage: number;
  slide_index: number | null;
  slides_count: number | null;
  average_duration: string | number;
}

interface ReadingDepthFunnelProps {
  items?: TPerformanceFunnelOverviewItem[];
  isLoading: boolean;
  isError: boolean;
}

const ReadingDepthRow = ({
  item,
  isMissing,
}: {
  item: TReadingDepthItem;
  isMissing?: boolean;
}) => {
  const percentage = item.percentage ?? 0;

  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-p-2 tw-rounded-xl",
        isMissing && "tw-opacity-50",
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-w-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
          {typeof item.slide_index === "number" && (
            <span className="tw-rounded-full tw-bg-blue-100 tw-px-1.5 tw-py-0.5 tw-font-sans tw-text-[10px] tw-font-medium tw-text-blue-500 tw-flex-shrink-0">
              Slide #{item.slide_index + 1}
            </span>
          )}
          <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-font-medium tw-truncate tw-min-w-0 tw-flex-1">
            {item.event}
          </span>
          <span className="tw-font-sans tw-text-xs tw-text-blue-800 tw-text-right tw-flex-shrink-0">
            {percentage > 0
              ? `${percentage}% (avg. ${item.average_duration})`
              : "0%"}
          </span>
        </div>
        <div className="tw-h-1.5 tw-relative tw-w-full tw-rounded-full">
          {/* Background bar */}
          <div className="tw-absolute tw-inset-0 tw-bg-blue-400/20 tw-rounded-full" />
          {/* Progress bar */}
          {percentage > 0 && (
            <div
              className="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-[#610CFF] tw-rounded-full"
              style={{ width: `${percentage}%` }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="tw-rounded-lg tw-outline tw-outline-1 tw-py-5 tw-outline-blue/5 tw-bg-white tw-relative">
      <span className="tw-pb-4 tw-text-sm tw-font-sans tw-font-semibold tw-text-blue tw-block tw-px-5">
        Reading depth funnel
      </span>
      {children}
    </div>
  );
};

const TableContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <ScrollArea className="tw-px-5 tw-h-[370px]">
      <div className="tw-min-w-0 tw-w-full tw-flex tw-flex-col tw-gap-2">
        {children}
      </div>
    </ScrollArea>
  );
};

export const ReadingProgressDepthFunnel = ({
  items,
  isLoading,
  isError,
}: ReadingDepthFunnelProps) => {
  // Transform API data to component format if needed
  const transformedItems = useMemo(() => {
    if (!items?.length) return [];

    // Group events by session_id and calculate time spent on each event
    const sessionGroups = new Map<string, TPerformanceFunnelOverviewItem[]>();

    items.forEach((item) => {
      const sessionId = item.session_id;
      if (!sessionGroups.has(sessionId)) {
        sessionGroups.set(sessionId, []);
      }
      sessionGroups.get(sessionId)!.push(item);
    });

    // Calculate time spent on each event for each session
    const eventDurations = new Map<string, number[]>();

    sessionGroups.forEach((sessionEvents) => {
      // Sort events by timestamp within the session
      const sortedEvents = [...sessionEvents].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );

      // Calculate time between consecutive events
      for (let i = 0; i < sortedEvents.length; i++) {
        const currentEvent = sortedEvents[i];
        const nextEvent = sortedEvents[i + 1];

        if (nextEvent) {
          const duration =
            (new Date(nextEvent.timestamp).getTime() -
              new Date(currentEvent.timestamp).getTime()) /
            1000; // Convert to seconds

          if (!eventDurations.has(currentEvent.event)) {
            eventDurations.set(currentEvent.event, []);
          }
          eventDurations.get(currentEvent.event)!.push(duration);
        }
      }
    });

    // Calculate average duration for each unique event
    const eventAverages = new Map<string, number>();
    eventDurations.forEach((durations, event) => {
      const avg = durations.reduce((sum, d) => sum + d, 0) / durations.length;
      eventAverages.set(event, avg);
    });

    // Calculate percentage for each event
    // Group by event and count unique visitors (one visitor = one event, regardless of how many times they triggered it)
    const eventVisitors = new Map<string, Set<string>>();
    const eventSlideIndex = new Map<string, number | null>();
    const allVisitors = new Set<string>();

    items.forEach((item) => {
      if (!eventVisitors.has(item.event)) {
        eventVisitors.set(item.event, new Set());
        eventSlideIndex.set(item.event, item.slide_index);
      }
      eventVisitors.get(item.event)!.add(item.visitor_id);
      allVisitors.add(item.visitor_id);
    });

    // Total unique visitors across all events
    const totalUniqueVisitors = allVisitors.size;

    // Filter out unwanted events after calculations
    const excludedEvents = [
      "visibility",
      "page load",
      "view_slide_1",
      "view_last_slide",
    ];

    // Get unique events and calculate percentages, filtering out excluded events
    const uniqueEvents = Array.from(eventVisitors.keys())
      .filter((event) => !excludedEvents.includes(event))
      .map((event) => {
        const uniqueVisitorCount = eventVisitors.get(event)!.size;
        const percentage =
          totalUniqueVisitors > 0
            ? (uniqueVisitorCount / totalUniqueVisitors) * 100
            : 0;

        return {
          event,
          percentage,
          uniqueVisitorCount,
          slide_index: eventSlideIndex.get(event),
        };
      });

    if (!uniqueEvents.length) return [];

    // Transform to ReadingDepthItem format
    const baseItems: TReadingDepthItem[] = uniqueEvents.map((item) => {
      const avgDuration = eventAverages.get(item.event) || 0;
      const formattedDuration =
        avgDuration >= 60
          ? `${Math.floor(avgDuration / 60)}m ${Math.round(avgDuration % 60)}s`
          : `${Math.round(avgDuration)}s`;

      return {
        event: item.event,
        percentage: Math.round(item.percentage * 10) / 10, // Round to 1 decimal
        slide_index: item.slide_index ?? null,
        slides_count: null,
        average_duration: formattedDuration,
      };
    });

    // Sort by slide_index (ascending) - funnel from slide 0 to n
    return baseItems.sort((a, b) => {
      // Handle null values - put them at the end
      if (a.slide_index === null && b.slide_index === null) return 0;
      if (a.slide_index === null) return 1;
      if (b.slide_index === null) return -1;
      return a.slide_index - b.slide_index;
    });
  }, [items]);

  const hasItems = transformedItems && transformedItems.length > 0;

  if (isError) {
    return (
      <Container>
        <ErrorStateMessage />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <TableContainer>
          <ReadingDepthFunnelSkeleton />
        </TableContainer>
      </Container>
    );
  }

  if (!hasItems) {
    return (
      <Container>
        <EmptyStateMessage
          title="No reading data available"
          description="Reading depth information will appear here once the session is complete."
          className="tw-max-w-72 tw-mx-auto tw-h-[370px]"
        />
      </Container>
    );
  }

  return (
    <Container>
      <TableContainer>
        {transformedItems.map((item, index) => (
          <ReadingDepthRow
            key={`${item.slide_index ?? index}`}
            item={item}
            isMissing={item.percentage === 0}
          />
        ))}
      </TableContainer>
    </Container>
  );
};

const ReadingDepthFunnelSkeleton = () => {
  return (
    <>
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          className="tw-animate-skeleton-loading tw-flex tw-items-center tw-p-2 tw-rounded-xl"
        >
          <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
              <span className="tw-h-1.5 tw-w-48 tw-rounded-lg tw-bg-blue-100/50 tw-block tw-flex-1" />
              <span className="tw-h-1.5 tw-w-24 tw-rounded-lg tw-bg-blue-100/50 tw-block tw-flex-shrink-0" />
            </div>
            <div className="tw-h-1.5 tw-w-full tw-rounded-full tw-bg-blue-100/50 tw-block" />
          </div>
        </div>
      ))}
    </>
  );
};
